import ButtonLink from 'components/ButtonLink';
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { CandidateType } from '../Candidates/Candidates';
import { RecipientRow } from './RecipientRow';
import { CustomSplitRow, HR, Recipients, TotalRow } from './styled';
import { CurrencyInput } from './CurrencyInput';
import { formatNumberAsCurrency, splitAmountRounded } from 'helpers/utils';
import { ButtonBrand } from 'components/ButtonBrand';
import { exceedsContributionLimit, getSelectedCandidates } from 'helpers/donate';
import classNames from 'classnames';
import { CustomLinkCopy } from './CustomLinkCopy';
import { ADMINS_ONLY, getIsFeatureOn } from 'helpers/featureFlags';
import { getOnChangeCandidateAmount } from './utils';
import { IAllocationType, IRecipientGroup } from 'store/donatePage/types';
import { trackEvent } from 'helpers/mixpanelEvents';

const ordinals = ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'];

const getOrdinal = (num: number) => {
  if (num < 1 || num > ordinals.length) {
    return '';
  }
  return ordinals[num - 1];
};

export const getGroupAllocationText = (
  allocationType: IAllocationType,
  currentGroup: IRecipientGroup,
  recipientGroups: IRecipientGroup[],
  clientName = 'the organization'
) => {
  if (allocationType === 'WEIGHTED') {
    return `This group will receive ${currentGroup.weight_percent}% of your total contribution, split evenly.`;
  }
  // else WATERFALL
  if (currentGroup.priority === 1) {
    return `This group has been prioritized by ${clientName}. They will receive up to the first ${formatNumberAsCurrency(
      (currentGroup.donation_limit_cents ?? 0) / 100
    )} of your contribution, split evenly.`;
  }
  if (currentGroup.priority === recipientGroups.length) {
    const previousGroupName =
      recipientGroups[currentGroup.priority - 2].group_label ||
      `Group ${recipientGroups.length - 1}`;
    return `This group is ${getOrdinal(
      currentGroup.priority
    )} priority by your organization. They will receive the rest of your contribution after ${previousGroupName}.`;
  }
  return `This group is ${getOrdinal(
    currentGroup.priority
  )} priority by ${clientName}. They will receive the next ${formatNumberAsCurrency(
    (currentGroup.donation_limit_cents ?? 0) / 100
  )} of your contribution, split evenly.`;
};

export const getSplitText = (
  allocationType: IAllocationType,
  splitValue: string,
  recipientGroups: IRecipientGroup[] = [],
  clientName = 'the organization',
  // EVEN-only context: contribution limits can cap or skew an even split, which
  // makes the "increments of $X" message inaccurate. When present, these drive the
  // correct single message so it never conflicts with a separately-rendered one.
  evenSplitContext: { overageMax?: number; limitsAffectSplit?: boolean } = {}
) => {
  if (allocationType === 'WATERFALL') {
    const firstGroupName = recipientGroups[0]?.group_label || 'Group 1';
    return `Your contribution will be split according to ${clientName}'s allocation guidelines. ${firstGroupName} is top priority. If ${firstGroupName}'s contribution limit is reached, your remaining contribution will be allocated to additional Groups.`;
  }
  if (allocationType === 'WEIGHTED') {
    return `Your contribution will be split according to ${clientName}'s allocation recommendation. The total amount is split across all groups based on the weighting assigned to ensure your dollars go where they are most effective.`;
  }
  // EVEN split. Resolve most-specific first: an overage (requested exceeds the
  // recipients' combined limit) beats a skewed-but-fully-allocated split, which
  // beats a clean even split.
  if (evenSplitContext.overageMax != null) {
    return `The maximum donation to these recipients is ${formatNumberAsCurrency(
      evenSplitContext.overageMax
    )}.`;
  }
  if (evenSplitContext.limitsAffectSplit) {
    return "Your contribution will be split based on each recipient's contribution limit. Customize below.";
  }
  return `Your contribution will be split in increments of ${splitValue} unless customized below.`;
};

interface Props {
  allocationType?: IAllocationType;
  candidates: CandidateType[];
  donationTotal?: number;
  overageMax?: number;
  recipientGroups?: IRecipientGroup[];
  resetDonationTotal: (amount?: number) => void;
  setCandidates: Dispatch<SetStateAction<CandidateType[]>>;
  setDonationTotal: Dispatch<SetStateAction<number | undefined>>;
  showCustomSplit: boolean;
}

export const CustomSplit: FC<Props> = ({
  allocationType = 'EVEN',
  candidates,
  donationTotal,
  overageMax,
  recipientGroups = [],
  resetDonationTotal,
  setCandidates,
  setDonationTotal,
  showCustomSplit,
}) => {
  const [isSplitVisible, setIsSplitVisible] = useState(false);
  const [tempDonationTotal, setTempDonationTotal] = useState(donationTotal);

  const selectedCandidates = useMemo(() => getSelectedCandidates(candidates), [candidates]);

  const evenShare = selectedCandidates.length
    ? (donationTotal ?? 0) / selectedCandidates.length
    : 0;

  // Even without an overage, a contribution limit can force an uneven split (the
  // total is fully allocated but not in equal increments). Detect it by checking
  // whether an even share would exceed any selected recipient's limit — this drives
  // the split message away from the inaccurate "increments of $X" wording.
  const limitsAffectSplit = useMemo(
    () =>
      allocationType === 'EVEN' &&
      selectedCandidates.some(candidate => exceedsContributionLimit(candidate, evenShare)),
    [allocationType, selectedCandidates, evenShare]
  );

  useEffect(() => {
    setTempDonationTotal(donationTotal);
  }, [donationTotal]);

  useEffect(() => {
    if (showCustomSplit) {
      setIsSplitVisible(showCustomSplit);
    }
  }, [showCustomSplit]);

  const onCustomizeSplitClick = () => {
    setIsSplitVisible(isVisible => !isVisible);
  };

  const onChangeTotalAmount = (value?: number) => {
    setTempDonationTotal(Number(value || 0));
  };

  const onResetDonationTotal = () => {
    resetDonationTotal(tempDonationTotal);
  };

  const getSplitIncrementValue = () => {
    const num = splitAmountRounded(donationTotal || 0, selectedCandidates.length)[0];
    return formatNumberAsCurrency(num, {
      trailingZeroDisplay: 'auto',
    });
  };

  const onEditComplete =
    allocationType !== 'EVEN'
      ? () => {
          trackEvent('Donate Page Split Amount Edited', {
            allocation_type: allocationType,
          });
        }
      : undefined;

  return (
    <>
      <CustomSplitRow>
        {getIsFeatureOn(ADMINS_ONLY) && selectedCandidates.length >= 1 && (
          <CustomLinkCopy candidates={candidates} />
        )}
        {selectedCandidates.length > 1 && allocationType === 'EVEN' && (
          <ButtonLink onClick={onCustomizeSplitClick}>Customize Split</ButtonLink>
        )}
      </CustomSplitRow>
      {(isSplitVisible || allocationType !== 'EVEN') && (
        <>
          <HR />
          <p className="body-text">Your total contribution:</p>
          <TotalRow>
            <CurrencyInput
              id="custom-split-total"
              value={tempDonationTotal}
              onChange={onChangeTotalAmount}
            />
            <ButtonBrand $secondary={true} $hasBorder={true} onClick={onResetDonationTotal}>
              Split
            </ButtonBrand>
          </TotalRow>
          {(allocationType !== 'EVEN' || selectedCandidates.length > 0) && (
            <div className={classNames('utility-text', { 'color-errorRed': overageMax != null })}>
              {getSplitText(allocationType, getSplitIncrementValue(), recipientGroups, undefined, {
                overageMax,
                limitsAffectSplit,
              })}
            </div>
          )}
          {allocationType === 'EVEN' || !recipientGroups.length ? (
            <Recipients>
              {candidates.map((candidate, index) => (
                <RecipientRow
                  key={candidate.de_id}
                  candidate={candidate}
                  index={index}
                  onChangeAmount={getOnChangeCandidateAmount(
                    candidates,
                    setCandidates,
                    setDonationTotal
                  )}
                  onEditComplete={onEditComplete}
                />
              ))}
            </Recipients>
          ) : (
            <div className="recipient-groups-container">
              {recipientGroups.map(recipientGroup => (
                <div key={recipientGroup.id} className="recipient-group">
                  <div className="eyebrow-text color-oathBlue mb3">
                    {recipientGroup.group_label || `Group ${recipientGroup.priority}`}
                  </div>
                  <div className="utility-text">
                    {getGroupAllocationText(allocationType, recipientGroup, recipientGroups)}
                  </div>
                  <div className="recipients-container">
                    {recipientGroup.recipient_ids
                      .map(recipientId =>
                        candidates.find(candidate => candidate.recipient_id === recipientId)
                      )
                      .filter((candidate): candidate is CandidateType => candidate !== undefined)
                      .map(candidate => (
                        <RecipientRow
                          key={candidate.de_id}
                          candidate={candidate}
                          showPhoto={true}
                          index={candidates.findIndex(
                            c => c.recipient_id === candidate.recipient_id
                          )}
                          onChangeAmount={getOnChangeCandidateAmount(
                            candidates,
                            setCandidates,
                            setDonationTotal
                          )}
                          onEditComplete={onEditComplete}
                        />
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};
